import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Search, Building2, Pencil, AlertCircle, Trash2, Merge, X, CalendarClock, BellRing, Plus, Download, ChevronDown, ChevronRight, Sparkles, Printer } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useShiftSelection } from "@/lib/useShiftSelection";
import { formatMoney } from "@/components/vendors/utils";
import { FilterChip } from "@/components/vendors/FilterChip";
import { VendorMonthsBreakdown } from "@/components/vendors/VendorMonthsBreakdown";
import { EditInvoiceModal } from "@/components/invoices/EditInvoiceModal";

export default function Vendors() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const baseCurrency = user?.base_currency || "EUR";
  const [vendors, setVendors] = useState([]);
  // Iter 82.7k — total income (base imponible) for the same period, so we
  // can render "% del vendor sobre la facturación" in the base column.
  const [incomeSubtotalPeriod, setIncomeSubtotalPeriod] = useState(0);
  const [q, setQ] = useState("");
  const [hasCifFilter, setHasCifFilter] = useState("all"); // all | with | without
  const [sortBy, setSortBy] = useState("total"); // total | subtotal | count | name | last_invoice
  const [sortOrder, setSortOrder] = useState("desc"); // desc | asc
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear())); // default: current year
  const [loading, setLoading] = useState(true);

  // Expanded row state: vendor group_key -> { loading, year, months[], totals }
  const [expanded, setExpanded] = useState({});
  // iter 82.7s — abrir EditInvoiceModal in-place para no colapsar el expandido
  // del proveedor al editar/borrar. Guardamos también qué group_key refrescar
  // tras el guardado para no perder el panel abierto.
  const [editingInvoice, setEditingInvoice] = useState(null); // objeto invoice completo
  const [editingVendorKey, setEditingVendorKey] = useState(null);
  const [editingLoading, setEditingLoading] = useState(false);

  const openInvoiceEdit = useCallback(async (inv, vendorKey) => {
    setEditingLoading(true);
    setEditingVendorKey(vendorKey);
    try {
      const { data } = await api.get(`/invoices/${inv.id}`);
      setEditingInvoice(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setEditingVendorKey(null);
    } finally {
      setEditingLoading(false);
    }
  }, []);

  // Edit modal state
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editTaxId, setEditTaxId] = useState("");
  const [saving, setSaving] = useState(false);

  // Merge modal state
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeName, setMergeName] = useState("");
  const [mergeTaxId, setMergeTaxId] = useState("");

  // Add-manual-vendor modal
  const [manualOpen, setManualOpen] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualTaxId, setManualTaxId] = useState("");
  const [manualRecurring, setManualRecurring] = useState(true);

  // Expected vendors (recurring monthly invoicing). Keyed by group_key.
  const [expectedByKey, setExpectedByKey] = useState({}); // group_key → {id, has_invoice_this_month, ytd_invoice_months, expected_count_ytd, missing_ytd, last_invoice_date}
  const [expectedYtd, setExpectedYtd] = useState(0);      // months elapsed in current year
  const [expectedTotalMissingYtd, setExpectedTotalMissingYtd] = useState(0);
  const [expectedBusy, setExpectedBusy] = useState(null); // group_key currently being toggled

  // Multi-selection (Shift+click range supported). The hook reads each item's
  // `id` or `group_key`; we use `group_key` directly.
  const { selectedIds, setSelectedIds, isSelected, toggle, toggleAll, allSelected, clear } = useShiftSelection();
  const items = vendors;
  const allSelectedOnPage = allSelected(items);

  // Dismissible "Proveedores similares" banner. Once dismissed for a session,
  // the banner stays hidden until reload (user was clear that this is optional).
  const [similarDismissed, setSimilarDismissed] = useState(false);

  // Sort helpers: click header to set sort field; second click toggles direction.
  const handleSortClick = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "desc" ? "asc" : "desc");
    } else {
      setSortBy(field);
      // Sensible default direction per field
      setSortOrder(field === "name" ? "asc" : "desc");
    }
  };
  const sortArrow = (field) => {
    if (sortBy !== field) return "";
    return sortOrder === "desc" ? "▼" : "▲";
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortBy, sort_order: sortOrder };
      if (q) params.q = q;
      if (hasCifFilter === "with") params.has_cif = true;
      if (hasCifFilter === "without") params.has_cif = false;
      if (yearFilter) params.year = yearFilter;
      const { data } = await api.get("/vendors", { params });
      setVendors(data.vendors || []);
      setIncomeSubtotalPeriod(Number(data.income_subtotal_period || 0));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [q, hasCifFilter, sortBy, sortOrder, yearFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = async (groupKey) => {
    // Atomic decision INSIDE the state update so React never picks a stale
    // snapshot. Collapse ALWAYS if the row is already expanded — even while
    // its data is still being fetched. Previously we required !loading which
    // meant a second quick click during the fetch would try to re-open
    // instead of closing.
    let willCollapse = false;
    setExpanded((prev) => {
      if (prev[groupKey]) {
        willCollapse = true;
        const { [groupKey]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [groupKey]: { loading: true } };
    });
    if (!willCollapse) {
      await refreshExpanded(groupKey);
    }
  };

  // Bulk collapse all currently expanded rows in a single state update.
  const collapseAll = () => setExpanded({});

  // Bulk expand every visible vendor (skips ghosts + already-loaded rows).
  // Batches network requests to avoid a burst of ~50 parallel calls: we run
  // them 5 at a time. UX-wise the user sees rows populate progressively.
  const expandAllVisible = async () => {
    const targets = (vendors || []).filter((v) => !v._ghost && !expanded[v.group_key]);
    if (targets.length === 0) return;
    // Mark all as loading up front so chevrons flip immediately.
    setExpanded((prev) => {
      const next = { ...prev };
      for (const v of targets) next[v.group_key] = { loading: true };
      return next;
    });
    const BATCH = 5;
    for (let i = 0; i < targets.length; i += BATCH) {
      await Promise.all(targets.slice(i, i + BATCH).map((v) => refreshExpanded(v.group_key)));
    }
  };

  // Iter 65 — Bug fix: refetch every currently-expanded breakdown when the
  // user changes the year filter, otherwise the month grid keeps showing the
  // previous year's numbers and the user thinks facturas están perdidas.
  // Uses a ref to read the current `expanded` map without adding it to the
  // effect dependency list (would fire on every open/close).
  const expandedRef = React.useRef({});
  useEffect(() => { expandedRef.current = expanded; }, [expanded]);

  // Force re-fetch the expanded panel data without toggling. Used after
  // mutations (invoice deletion) so the month grid + totals stay accurate
  // without the user having to collapse/expand manually.
  // Iter 65 — Bug fix: uses the currently-selected `yearFilter` instead of a
  // hardcoded `new Date().getFullYear()`. Previously changing the year at
  // the top of the page kept showing invoices from the current year in the
  // per-vendor breakdown.
  const refreshExpanded = useCallback(async (groupKey, yearOverride) => {
    try {
      const year = yearOverride || yearFilter || String(new Date().getFullYear());
      const { data } = await api.get("/vendors/months", {
        params: { group_key: groupKey, year },
      });
      // Only persist the fetched data if the user hasn't collapsed the row
      // in the meantime. Otherwise we would re-expand a row the user just
      // closed. React functional updater sees the freshest state.
      setExpanded((prev) => {
        if (!prev[groupKey]) return prev;
        return { ...prev, [groupKey]: { loading: false, ...data } };
      });
    } catch (err) {
      toast.error(formatApiError(err));
      setExpanded((prev) => {
        const { [groupKey]: _drop, ...rest } = prev;
        return rest;
      });
    }
  }, [yearFilter]);

  // Effect defined AFTER refreshExpanded to avoid TDZ (const initialization).
  useEffect(() => {
    const openKeys = Object.keys(expandedRef.current);
    if (openKeys.length === 0) return;
    setExpanded((prev) => {
      const next = { ...prev };
      for (const k of openKeys) next[k] = { ...next[k], loading: true };
      return next;
    });
    (async () => {
      const BATCH = 5;
      for (let i = 0; i < openKeys.length; i += BATCH) {
        await Promise.all(openKeys.slice(i, i + BATCH).map((k) => refreshExpanded(k, yearFilter)));
      }
    })();
  }, [yearFilter, refreshExpanded]);

  const exportVendorsXlsx = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (hasCifFilter === "with") params.has_cif = true;
      if (hasCifFilter === "without") params.has_cif = false;
      const res = await api.get("/vendors/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `proveedores-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportVendorsMonthlyXlsx = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (hasCifFilter === "with") params.has_cif = true;
      if (hasCifFilter === "without") params.has_cif = false;
      // Default to selected year filter, or current year if "total histórico"
      params.year = yearFilter ? parseInt(yearFilter, 10) : new Date().getFullYear();
      const res = await api.get("/vendors/export-monthly", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `proveedores-desglose-${params.year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Load expected vendors and refresh after toggles
  const loadExpected = useCallback(async () => {
    try {
      const { data } = await api.get("/expected-vendors");
      setExpectedYtd(data.expected_count_ytd || 0);
      setExpectedTotalMissingYtd(data.missing_invoices_total || 0);
      const map = {};
      for (const it of (data.items || [])) map[it.group_key] = it;
      setExpectedByKey(map);
    } catch (err) {
      // Soft-fail: this is an optional feature, don't block the page
      console.warn("Failed to load expected vendors", err);
    }
  }, []);

  useEffect(() => { loadExpected(); }, [loadExpected]);

  const toggleExpected = async (vendor) => {
    setExpectedBusy(vendor.group_key);
    try {
      const flagged = expectedByKey[vendor.group_key];
      if (flagged) {
        await api.delete(`/expected-vendors/${flagged.id}`);
        toast.success(lang === "es" ? "Factura mensual desactivada" : "Monthly invoice disabled");
      } else {
        await api.post("/expected-vendors", {
          group_key: vendor.group_key,
          vendor_name: vendor.primary_name,
          vendor_tax_id: vendor.vendor_tax_id || null,
        });
        toast.success(lang === "es" ? "Marcado como factura mensual" : "Marked as monthly invoice");
      }
      await loadExpected();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExpectedBusy(null);
    }
  };

  // Bulk auto-detection: scans invoice history and marks any vendor with
  // recurring monthly pattern (≥3 months in the last 6) as expected. Prevents
  // silent gaps like the FERRER-April case where a missing month goes unnoticed
  // until the P&L review at year-end.
  const [autoDetectBusy, setAutoDetectBusy] = useState(false);
  const runAutoDetect = async () => {
    if (autoDetectBusy) return;
    setAutoDetectBusy(true);
    try {
      const { data } = await api.post("/expected-vendors/auto-detect");
      const summary = lang === "es"
        ? `${data.added_count} proveedor(es) marcado(s) como mensual · ${data.already_expected_count} ya estaban · ${data.skipped_count} sin patrón mensual`
        : `${data.added_count} vendor(s) flagged monthly · ${data.already_expected_count} already flagged · ${data.skipped_count} no monthly pattern`;
      toast.success(summary, { duration: 6000 });
      await loadExpected();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAutoDetectBusy(false);
    }
  };

  const missingThisMonth = Object.values(expectedByKey).filter((e) => (e.missing_ytd || 0) > 0);

  // Compute the displayed items list, merging "ghost vendors" (flagged but
  // no invoices uploaded yet) into the real vendor groups.
  const itemsWithGhosts = useMemo(() => {
    const realKeys = new Set((items || []).map((v) => v.group_key));
    const ghosts = Object.values(expectedByKey)
      .filter((e) => !realKeys.has(e.group_key))
      .map((e) => ({
        group_key: e.group_key,
        vendor_tax_id: e.vendor_tax_id || null,
        primary_name: e.vendor_name || "(Sin proveedor)",
        name_variants: [],
        invoice_count: 0,
        total_amount: 0,
        total_subtotal: 0,
        first_invoice_date: null,
        last_invoice_date: null,
        currencies: [],
        _ghost: true,
      }));
    return [...(items || []), ...ghosts];
  }, [items, expectedByKey]);

  const submitManual = async () => {
    const name = manualName.trim();
    const taxId = manualTaxId.trim().toUpperCase().replace(/\s+/g, "");
    if (!name && !taxId) {
      toast.error(lang === "es" ? "Indica al menos nombre o CIF." : "Provide at least a name or tax ID.");
      return;
    }
    // group_key follows the same convention as the backend:
    //   - "cif::TAXID" (upper-cased) when CIF provided
    //   - otherwise "name::lowercase-name"
    const groupKey = taxId
      ? taxId  // backend uses the upper-cased CIF directly as group key
      : `name::${name.toLowerCase()}`;
    setSaving(true);
    try {
      // 1) Always create the expected_vendor record so the row appears in
      //    the table even without any invoices yet.
      await api.post("/expected-vendors", {
        group_key: groupKey,
        vendor_name: name || null,
        vendor_tax_id: taxId || null,
      });
      // 2) If the user un-checked "Esperar factura mensual", undo right away
      //    so the toggle stays consistent with their intent.
      if (!manualRecurring) {
        await loadExpected();
        const flag = Object.values(expectedByKey).find((e) => e.group_key === groupKey);
        if (flag) {
          await api.delete(`/expected-vendors/${flag.id}`);
        }
      }
      toast.success(lang === "es" ? "Proveedor añadido" : "Vendor added");
      setManualOpen(false);
      setManualName("");
      setManualTaxId("");
      setManualRecurring(true);
      await Promise.all([load(), loadExpected()]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (vendor) => {
    setEditing(vendor);
    setEditName(vendor.primary_name === "(Sin proveedor)" ? "" : vendor.primary_name);
    setEditTaxId(vendor.vendor_tax_id || "");
  };

  const closeEdit = () => {
    if (saving) return;
    setEditing(null);
    setEditName("");
    setEditTaxId("");
  };

  const submitEdit = async () => {
    if (!editing) return;
    const newName = editName.trim();
    const newTaxId = editTaxId.trim().toUpperCase().replace(/\s+/g, "");
    if (!newName && !newTaxId) {
      toast.error(lang === "es" ? "Indica un nombre o CIF nuevo." : "Provide a new name or tax ID.");
      return;
    }
    setSaving(true);
    try {
      const payload = { group_key: editing.group_key };
      if (newName) payload.new_name = newName;
      if (newTaxId) payload.new_tax_id = newTaxId;
      const { data } = await api.patch("/vendors", payload);
      if (data.modified === 0) {
        toast.info(lang === "es" ? "Sin cambios que aplicar." : "No changes applied.");
      } else {
        toast.success(
          lang === "es"
            ? `Actualizadas ${data.modified} facturas`
            : `Updated ${data.modified} invoices`,
        );
      }
      closeEdit();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // -------- Bulk actions --------
  const selectedVendors = vendors.filter((v) => selectedIds.has(v.group_key));
  const selectedInvoiceCount = selectedVendors.reduce((acc, v) => acc + v.invoice_count, 0);

  // -------- Similar-vendor detection --------
  // Heuristic banner: pairs of vendors whose normalized names look near-
  // identical (typo, missing CIF, "SL" variants). Runs client-side over the
  // already-loaded list, so it's O(n²) but n is small (≤ ~200 rows).
  const similarPairs = useMemo(() => {
    if (!vendors || vendors.length < 2) return [];
    const norm = (s) => (s || "")
      .toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")     // strip diacritics
      .replace(/\b(s\.?l\.?u?|s\.?a\.?u?|s\.?a\.?s|sll|slne|sccm?|cb|coop|comunidad de bienes|de bienes|inc|ltd|llc)\b/gi, "")
      .replace(/[.,\-·()/&+]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    // Levenshtein with early exit at maxDist+1.
    const editDistance = (a, b, maxDist) => {
      const m = a.length, n = b.length;
      if (Math.abs(m - n) > maxDist) return maxDist + 1;
      const prev = new Array(n + 1);
      const curr = new Array(n + 1);
      for (let j = 0; j <= n; j++) prev[j] = j;
      for (let i = 1; i <= m; i++) {
        curr[0] = i;
        let rowMin = curr[0];
        for (let j = 1; j <= n; j++) {
          const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
          curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
          if (curr[j] < rowMin) rowMin = curr[j];
        }
        if (rowMin > maxDist) return maxDist + 1;
        for (let j = 0; j <= n; j++) prev[j] = curr[j];
      }
      return prev[n];
    };
    const enriched = vendors
      .filter((v) => !v._ghost && v.primary_name && v.primary_name !== "(Sin proveedor)")
      .map((v) => ({ v, norm: norm(v.primary_name) }))
      .filter((x) => x.norm.length >= 4);
    const pairs = [];
    for (let i = 0; i < enriched.length; i++) {
      for (let j = i + 1; j < enriched.length; j++) {
        const a = enriched[i], b = enriched[j];
        if (a.v.group_key === b.v.group_key) continue;
        // Skip when BOTH have a real CIF and they're different — the CIF is
        // the source of truth, so different CIFs = different companies.
        if (a.v.vendor_tax_id && b.v.vendor_tax_id && a.v.vendor_tax_id !== b.v.vendor_tax_id) continue;
        const la = a.norm.length, lb = b.norm.length;
        // Adaptive threshold: ≤2 edits for short names, up to 3 for longer.
        const maxDist = Math.max(1, Math.min(3, Math.floor(Math.max(la, lb) / 6)));
        const d = editDistance(a.norm, b.norm, maxDist);
        const isPrefix = la >= 8 && lb >= 8 && (a.norm.startsWith(b.norm) || b.norm.startsWith(a.norm));
        if (d <= maxDist || isPrefix) {
          pairs.push({ a: a.v, b: b.v, distance: d, isPrefix });
        }
      }
    }
    // Sort: exact-match candidates first (distance 0), then by min distance
    pairs.sort((x, y) => x.distance - y.distance);
    return pairs;
  }, [vendors]);

  // "Fusionar" from the similarity banner — pre-selects the pair and opens
  // the existing merge modal so the user reviews before committing.
  const openMergeForPair = (pair) => {
    setSelectedIds(new Set([pair.a.group_key, pair.b.group_key]));
    // Fire on next tick so `selectedVendors` recomputes before openMerge reads it.
    setTimeout(() => openMerge(), 0);
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const ok = window.confirm(
      lang === "es"
        ? `¿Borrar ${selectedIds.size} proveedor(es) y sus ${selectedInvoiceCount} facturas? Esta acción no se puede deshacer.`
        : `Delete ${selectedIds.size} vendor(s) and their ${selectedInvoiceCount} invoices? This cannot be undone.`,
    );
    if (!ok) return;
    setSaving(true);
    try {
      const { data } = await api.post("/vendors/bulk-delete", {
        group_keys: Array.from(selectedIds),
      });
      toast.success(
        lang === "es"
          ? `Borradas ${data.deleted} facturas`
          : `Deleted ${data.deleted} invoices`,
      );
      clear();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openMerge = () => {
    if (selectedIds.size < 2) {
      toast.error(
        lang === "es"
          ? "Selecciona al menos 2 proveedores para fusionar."
          : "Select at least 2 vendors to merge.",
      );
      return;
    }
    // Pre-fill with the most descriptive picks
    const withCif = selectedVendors.find((v) => v.vendor_tax_id);
    const longestName = selectedVendors
      .map((v) => v.primary_name)
      .filter(Boolean)
      .sort((a, b) => b.length - a.length)[0] || "";
    setMergeName(longestName === "(Sin proveedor)" ? "" : longestName);
    setMergeTaxId(withCif?.vendor_tax_id || "");
    setMergeOpen(true);
  };

  const submitMerge = async () => {
    const name = mergeName.trim();
    const taxId = mergeTaxId.trim().toUpperCase().replace(/\s+/g, "");
    if (!name && !taxId) {
      toast.error(lang === "es" ? "Indica un nombre o CIF destino." : "Provide a target name or tax ID.");
      return;
    }
    setSaving(true);
    try {
      const payload = { group_keys: Array.from(selectedIds) };
      if (name) payload.target_name = name;
      if (taxId) payload.target_tax_id = taxId;
      const { data } = await api.post("/vendors/merge", payload);
      toast.success(
        lang === "es"
          ? `Fusionados en 1 proveedor (${data.modified} facturas)`
          : `Merged into 1 vendor (${data.modified} invoices)`,
      );
      setMergeOpen(false);
      clear();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-2">
            {lang === "es" ? "Catálogo" : "Catalog"}
          </p>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none">
            {lang === "es" ? "Proveedores" : "Vendors"}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
            {lang === "es"
              ? "Tus proveedores agrupados por CIF. Selecciona varias filas (clic + Shift+clic para rangos) y fusiónalas o bórralas en lote."
              : "Suppliers grouped by tax ID. Select multiple rows (click + Shift+click for ranges) to merge or delete them in bulk."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
            <Input
              data-testid="vendors-search"
              placeholder={lang === "es" ? "Buscar proveedor o CIF..." : "Search vendor or tax ID..."}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 w-72"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={exportVendorsXlsx}
            data-testid="vendors-export-xlsx-btn"
          >
            <Download size={14} className="mr-1.5" />
            Excel
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={exportVendorsMonthlyXlsx}
            data-testid="vendors-export-monthly-xlsx-btn"
            title={lang === "es"
              ? "Descarga Excel con una fila por proveedor y 12 columnas mensuales + total anual"
              : "Download Excel with one row per vendor and 12 monthly columns + annual total"}
          >
            <Download size={14} className="mr-1.5" />
            {lang === "es" ? "Excel desglosado" : "Excel monthly"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.open("/proveedores/imprimir", "_blank")}
            data-testid="vendors-print-btn"
            title={lang === "es"
              ? "Abrir listado imprimible con nombre / CIF / dirección / tipo de producto"
              : "Open printable directory with name / CIF / address / product type"}
          >
            <Printer size={14} className="mr-1.5" />
            {lang === "es" ? "Imprimir listado" : "Print directory"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={runAutoDetect}
            disabled={autoDetectBusy}
            data-testid="vendors-auto-detect-btn"
            title={lang === "es"
              ? "Analiza tus facturas y marca como 'mensual' los proveedores con ≥3 meses de facturas en los últimos 6 meses"
              : "Scan invoices and flag vendors with ≥3 monthly invoices in the last 6 months"}
          >
            <Sparkles size={14} className="mr-1.5" />
            {autoDetectBusy
              ? (lang === "es" ? "Detectando..." : "Detecting...")
              : (lang === "es" ? "Auto-detectar mensuales" : "Auto-detect monthly")}
          </Button>
          <Button
            size="sm"
            onClick={() => setManualOpen(true)}
            data-testid="vendors-add-manual-btn"
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          >
            <Plus size={14} className="mr-1.5" />
            {lang === "es" ? "Añadir proveedor" : "Add vendor"}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-[var(--text-muted)] mr-1">
          {lang === "es" ? "Filtro CIF:" : "Tax ID filter:"}
        </span>
        <FilterChip value="all" label={lang === "es" ? "Todos" : "All"} active={hasCifFilter === "all"} onClick={() => setHasCifFilter("all")} />
        <FilterChip value="with" label={lang === "es" ? "Con CIF" : "With tax ID"} active={hasCifFilter === "with"} onClick={() => setHasCifFilter("with")} />
        <FilterChip value="without" label={lang === "es" ? "Sin CIF" : "Without tax ID"} active={hasCifFilter === "without"} onClick={() => setHasCifFilter("without")} />

        <span className="text-xs text-[var(--text-muted)] mx-2">·</span>
        <span className="text-xs text-[var(--text-muted)] mr-1">
          {lang === "es" ? "Año:" : "Year:"}
        </span>
        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          data-testid="vendors-year-filter"
          className="px-2 py-1 text-xs rounded-md border border-[var(--border-color)] bg-[var(--surface)]"
        >
          <option value="">{lang === "es" ? "Total histórico" : "Historic total"}</option>
          {(() => {
            const cy = new Date().getFullYear();
            return [cy, cy - 1, cy - 2, cy - 3].map((y) => <option key={y} value={y}>{y}</option>);
          })()}
        </select>

        <span className="text-xs text-[var(--text-muted)] mx-2">·</span>
        <span className="text-xs text-[var(--text-muted)] mr-1">
          {lang === "es" ? "Ordenar por:" : "Sort by:"}
        </span>
        <select
          value={sortBy}
          onChange={(e) => {
            const v = e.target.value;
            setSortBy(v);
            // Sensible default direction per sort field
            if (v === "name") setSortOrder("asc");
            else setSortOrder("desc");
          }}
          data-testid="vendors-sort-by"
          className="px-2 py-1 text-xs rounded-md border border-[var(--border-color)] bg-[var(--surface)]"
        >
          <option value="name">{lang === "es" ? "Nombre" : "Name"}</option>
          <option value="total">{lang === "es" ? "Total facturado" : "Total billed"}</option>
          <option value="subtotal">{lang === "es" ? "Base imponible" : "Net amount"}</option>
          <option value="count">{lang === "es" ? "Nº facturas" : "Invoice count"}</option>
          <option value="last_invoice">{lang === "es" ? "Última factura" : "Last invoice"}</option>
        </select>
        <button
          onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
          data-testid="vendors-sort-order"
          className="px-2.5 py-1 text-xs rounded-md border border-[var(--border-color)] hover:bg-[var(--surface-hover)] font-medium"
          title={lang === "es" ? "Cambiar dirección" : "Toggle direction"}
        >
          {sortBy === "name"
            ? (sortOrder === "asc" ? "A→Z" : "Z→A")
            : (sortOrder === "desc" ? "↓ Mayor primero" : "↑ Menor primero")}
        </button>

        <span className="text-xs text-[var(--text-muted)] mx-2">·</span>
        {Object.keys(expanded).length > 0 ? (
          <button
            onClick={collapseAll}
            data-testid="vendors-collapse-all"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-[var(--border-color)] hover:bg-[var(--surface-hover)] font-medium"
            title={lang === "es" ? "Cerrar todos los desgloses" : "Collapse all breakdowns"}
          >
            <ChevronRight size={12} className="-rotate-90" />
            {lang === "es" ? `Contraer todo (${Object.keys(expanded).length})` : `Collapse all (${Object.keys(expanded).length})`}
          </button>
        ) : (
          <button
            onClick={expandAllVisible}
            disabled={(vendors || []).filter((v) => !v._ghost).length === 0}
            data-testid="vendors-expand-all"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-[var(--border-color)] hover:bg-[var(--surface-hover)] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            title={lang === "es" ? "Expandir el desglose mensual de todos los proveedores visibles" : "Expand monthly breakdown for all visible vendors"}
          >
            <ChevronDown size={12} />
            {lang === "es" ? "Expandir todos" : "Expand all"}
          </button>
        )}
      </div>

      {missingThisMonth.length > 0 && (
        <div
          className="flex items-start gap-3 px-4 py-3 rounded-lg bg-[var(--status-warning-bg)] border border-[var(--status-warning)]/40"
          data-testid="expected-vendors-alert"
        >
          <BellRing size={18} className="text-[var(--status-warning)] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[var(--status-warning)]">
              {lang === "es"
                ? `Faltan ${expectedTotalMissingYtd} factura(s) mensual(es) este año (a mes ${expectedYtd}/12)`
                : `Missing ${expectedTotalMissingYtd} monthly invoice(s) this year (through month ${expectedYtd}/12)`}
            </p>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">
              {missingThisMonth.slice(0, 5).map((m) => `${m.vendor_name || m.group_key} (${m.ytd_invoice_months}/${m.expected_count_ytd})`).join(" · ")}
              {missingThisMonth.length > 5 && ` (+${missingThisMonth.length - 5})`}
            </p>
          </div>
        </div>
      )}

      {similarPairs.length > 0 && !similarDismissed && (
        <div
          className="flex items-start gap-3 px-4 py-3 rounded-lg bg-[var(--surface)] border border-[var(--accent)]/60"
          data-testid="similar-vendors-alert"
        >
          <Merge size={18} className="text-[var(--accent)] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-2">
            <p className="text-sm font-medium">
              {lang === "es"
                ? `Detectados ${similarPairs.length} par(es) de proveedores similares — puede que sean el mismo`
                : `${similarPairs.length} similar vendor pair(s) detected — might be the same supplier`}
            </p>
            <ul className="space-y-1">
              {similarPairs.slice(0, 5).map((p, i) => (
                <li
                  key={`${p.a.group_key}::${p.b.group_key}`}
                  className="flex items-center justify-between gap-3 text-xs bg-[var(--surface-hover)] rounded px-2 py-1.5"
                  data-testid={`similar-pair-${i}`}
                >
                  <span className="min-w-0 flex-1 truncate">
                    <strong>{p.a.primary_name}</strong>
                    {p.a.vendor_tax_id && (
                      <span className="ml-1 font-mono text-[10px] text-[var(--text-muted)]">{p.a.vendor_tax_id}</span>
                    )}
                    <span className="mx-2 text-[var(--text-muted)]">↔</span>
                    <strong>{p.b.primary_name}</strong>
                    {p.b.vendor_tax_id && (
                      <span className="ml-1 font-mono text-[10px] text-[var(--text-muted)]">{p.b.vendor_tax_id}</span>
                    )}
                  </span>
                  <button
                    onClick={() => openMergeForPair(p)}
                    data-testid={`similar-pair-merge-${i}`}
                    className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded text-white bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-[11px] font-medium"
                    title={lang === "es" ? "Fusionar este par" : "Merge this pair"}
                  >
                    <Merge size={11} />
                    {lang === "es" ? "Fusionar" : "Merge"}
                  </button>
                </li>
              ))}
              {similarPairs.length > 5 && (
                <li className="text-[10px] text-[var(--text-muted)] italic px-2">
                  {lang === "es"
                    ? `+${similarPairs.length - 5} par(es) más… (ajusta el filtro para ver menos proveedores)`
                    : `+${similarPairs.length - 5} more pair(s)… (narrow the filter to reduce the list)`}
                </li>
              )}
            </ul>
          </div>
          <button
            onClick={() => setSimilarDismissed(true)}
            data-testid="similar-vendors-dismiss"
            className="shrink-0 p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-muted)]"
            title={lang === "es" ? "Ocultar aviso" : "Dismiss"}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div
          className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg bg-[var(--surface)] border border-[var(--accent)]"
          data-testid="vendors-bulk-bar"
        >
          <span className="text-xs text-[var(--text-secondary)]">
            {lang === "es"
              ? `${selectedIds.size} proveedor(es) · ${selectedInvoiceCount} facturas`
              : `${selectedIds.size} vendor(s) · ${selectedInvoiceCount} invoices`}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={openMerge}
              disabled={saving || selectedIds.size < 2}
              data-testid="vendors-bulk-merge"
            >
              <Merge size={12} className="mr-1" />
              {lang === "es" ? "Fusionar" : "Merge"}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={bulkDelete}
              disabled={saving}
              data-testid="vendors-bulk-delete"
            >
              <Trash2 size={12} className="mr-1" />
              {lang === "es" ? "Borrar" : "Delete"}
            </Button>
            <button
              onClick={clear}
              className="p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"
              data-testid="vendors-bulk-clear"
              title={lang === "es" ? "Limpiar selección" : "Clear selection"}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-3 overflow-x-auto">
        <table className="w-full text-sm" data-testid="vendors-table">
          <thead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
            <tr className="border-b border-[var(--border-color)]">
              <th className="text-left py-2 px-2 w-8">
                <input
                  type="checkbox"
                  checked={allSelectedOnPage}
                  onChange={() => toggleAll(items)}
                  data-testid="vendors-select-all"
                  className="rounded border-[var(--border-color)]"
                  aria-label="select all vendors"
                />
              </th>
              <th
                onClick={() => handleSortClick("name")}
                className="text-left py-2 px-2 font-medium cursor-pointer select-none hover:text-[var(--text-primary)]"
                data-testid="vendors-th-name"
                title={lang === "es" ? "Ordenar por nombre" : "Sort by name"}
              >
                {lang === "es" ? "Proveedor" : "Vendor"} {sortArrow("name")}
              </th>
              <th className="text-left py-2 px-2 font-medium">CIF / NIF / VAT</th>
              <th
                onClick={() => handleSortClick("count")}
                className="text-right py-2 px-2 font-medium cursor-pointer select-none hover:text-[var(--text-primary)]"
                data-testid="vendors-th-count"
                title={lang === "es"
                  ? (yearFilter ? `Nº facturas contabilizadas en ${yearFilter}` : "Nº facturas · todos los años")
                  : (yearFilter ? `Invoices booked in ${yearFilter}` : "Invoices · all-time")}
              >
                {yearFilter
                  ? (lang === "es" ? `Facturas ${yearFilter}` : `Invoices ${yearFilter}`)
                  : (lang === "es" ? "Facturas" : "Invoices")} {sortArrow("count")}
              </th>
              <th
                onClick={() => handleSortClick("subtotal")}
                className="text-right py-2 px-2 font-medium cursor-pointer select-none hover:text-[var(--text-primary)]"
                data-testid="vendors-th-subtotal"
                title={lang === "es" ? "Ordenar por base imponible" : "Sort by net"}
              >
                {yearFilter
                  ? (lang === "es" ? `Base imp. ${yearFilter}` : `Net ${yearFilter}`)
                  : (lang === "es" ? "Base imponible" : "Net (excl. VAT)")} {sortArrow("subtotal")}
              </th>
              <th
                onClick={() => handleSortClick("total")}
                className="text-right py-2 px-2 font-medium cursor-pointer select-none hover:text-[var(--text-primary)]"
                data-testid="vendors-th-total"
                title={lang === "es" ? "Ordenar por total" : "Sort by total"}
              >
                {yearFilter
                  ? (lang === "es" ? `Total ${yearFilter}` : `Total ${yearFilter}`)
                  : (lang === "es" ? "Total facturado" : "Total invoiced")} {sortArrow("total")}
              </th>
              {yearFilter && (
                <th
                  className="text-right py-2 px-2 font-medium"
                  data-testid="vendors-th-total-historic"
                  title={lang === "es" ? "Total histórico (todos los años)" : "Historic total (all years)"}
                >
                  {lang === "es" ? "Total histórico" : "Historic total"}
                </th>
              )}
              <th
                onClick={() => handleSortClick("last_invoice")}
                className="text-left py-2 px-2 font-medium cursor-pointer select-none hover:text-[var(--text-primary)]"
                data-testid="vendors-th-last"
                title={lang === "es" ? "Ordenar por última factura" : "Sort by last invoice"}
              >
                {lang === "es" ? "Última factura" : "Last invoice"} {sortArrow("last_invoice")}
              </th>
              <th className="text-center py-2 px-2 font-medium w-24" title={lang === "es" ? "Esperar factura mensual" : "Expect monthly invoice"}>
                {lang === "es" ? "Mensual" : "Monthly"}
              </th>
              <th className="text-right py-2 px-2 font-medium w-12"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={yearFilter ? 10 : 9} className="text-center py-10 text-[var(--text-muted)]">…</td></tr>
            )}
            {!loading && itemsWithGhosts.length === 0 && (
              <tr><td colSpan={yearFilter ? 10 : 9} className="text-center py-12 text-[var(--text-muted)]">
                {lang === "es" ? "Sin resultados." : "No results."}
              </td></tr>
            )}
            {!loading && itemsWithGhosts.map((v, idx) => {
              const expectedFlag = expectedByKey[v.group_key];
              const ytdComplete = !!expectedFlag && (expectedFlag.missing_ytd || 0) === 0;
              const ytdMissing = !!expectedFlag && (expectedFlag.missing_ytd || 0) > 0;
              return (
              <React.Fragment key={v.group_key}>
              <tr
                className={`border-b border-[var(--border-color)] transition-colors ${expanded[v.group_key] ? "bg-[var(--surface-hover)] border-l-2 border-l-[var(--primary)]" : "hover:bg-[var(--surface-hover)]"} ${isSelected(v.group_key) ? "bg-[var(--surface-hover)]" : ""} ${ytdComplete ? "bg-emerald-50/60 dark:bg-emerald-950/20" : ""} ${ytdMissing ? "bg-[var(--status-warning-bg)]/30" : ""} ${!v._ghost ? "cursor-pointer" : ""}`}
                data-testid={`vendor-row-${idx}`}
                onClick={(e) => {
                  // Ignore clicks on interactive children (buttons, links,
                  // inputs, selects) so their own handlers fire cleanly.
                  const tag = e.target.tagName;
                  const inInteractive = e.target.closest("button, a, input, select, textarea, [role='button']");
                  if (e.shiftKey && !["INPUT", "BUTTON", "A", "SELECT", "TEXTAREA"].includes(tag)) {
                    e.preventDefault();
                    window.getSelection?.()?.removeAllRanges?.();
                    toggle(v.group_key, idx, items, true);
                    return;
                  }
                  if (v._ghost || inInteractive) return;
                  toggleExpand(v.group_key);
                }}
              >
                <td className="py-2 px-2">
                  <input
                    type="checkbox"
                    checked={isSelected(v.group_key)}
                    onChange={() => {}}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (v._ghost) return; // ghosts can't be selected for bulk ops
                      toggle(v.group_key, idx, itemsWithGhosts, e.shiftKey);
                    }}
                    disabled={v._ghost}
                    data-testid={`vendor-select-${idx}`}
                    className="rounded border-[var(--border-color)] disabled:opacity-30"
                    aria-label={`select ${v.primary_name}`}
                  />
                </td>
                <td className="py-2 px-2">
                  <div className="flex items-center gap-2">
                    {!v._ghost ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleExpand(v.group_key); }}
                        data-testid={`vendor-expand-${idx}`}
                        className={`p-1 rounded shrink-0 transition-colors ${expanded[v.group_key] ? "text-[var(--primary)] bg-[var(--primary)]/10" : "text-[var(--text-muted)] hover:bg-[var(--surface-hover)]"}`}
                        aria-label={expanded[v.group_key] ? "Collapse" : "Expand"}
                        aria-expanded={!!expanded[v.group_key]}
                      >
                        <ChevronDown
                          size={14}
                          className={`transition-transform duration-200 ${expanded[v.group_key] ? "rotate-0" : "-rotate-90"}`}
                        />
                      </button>
                    ) : (
                      <Building2 size={14} className="text-[var(--text-muted)] shrink-0 ml-1.5" />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium truncate" data-testid={`vendor-name-${idx}`}>{v.primary_name}</p>
                      {v.name_variants.length > 0 && (
                        <p className="text-[10px] text-[var(--text-muted)] truncate" title={v.name_variants.join(" · ")}>
                          {lang === "es" ? "También como" : "Also as"}: {v.name_variants.slice(0, 2).join(", ")}
                          {v.name_variants.length > 2 && ` (+${v.name_variants.length - 2})`}
                        </p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="py-2 px-2 font-mono text-xs" data-testid={`vendor-tax-id-${idx}`}>
                  {v.vendor_tax_id || (
                    <span className="inline-flex items-center gap-1 text-amber-500" title={lang === "es" ? "Sin CIF" : "Missing tax ID"}>
                      <AlertCircle size={12} /> —
                    </span>
                  )}
                </td>
                <td className="py-2 px-2 text-right font-mono-num" data-testid={`vendor-count-${idx}`}>
                  {v.invoice_count}
                  {yearFilter && v.invoice_count_historic != null && v.invoice_count_historic !== v.invoice_count && (
                    <span
                      className="ml-1.5 text-[10px] text-[var(--text-muted)] font-normal"
                      title={lang === "es"
                        ? `${v.invoice_count_historic} facturas en total (todos los años)`
                        : `${v.invoice_count_historic} invoices total (all years)`}
                    >
                      / {v.invoice_count_historic}
                    </span>
                  )}
                </td>
                <td className="py-2 px-2 text-right font-mono-num" data-testid={`vendor-base-${idx}`}>
                  {formatMoney(v.total_subtotal, baseCurrency)}
                  {incomeSubtotalPeriod > 0 && v.total_subtotal > 0 && (
                    <span
                      className="ml-1.5 text-[10px] font-semibold text-[var(--brand-accent)] font-normal"
                      data-testid={`vendor-cost-ratio-${idx}`}
                      title={lang === "es"
                        ? `Base imponible del proveedor ÷ facturación ${yearFilter || "total"} sin IVA`
                        : `Vendor net ÷ ${yearFilter || "total"} income (excl. VAT)`}
                    >
                      · {(v.total_subtotal / incomeSubtotalPeriod * 100).toFixed(1)}%
                    </span>
                  )}
                </td>
                <td className="py-2 px-2 text-right font-mono-num font-semibold">{formatMoney(v.total_amount, baseCurrency)}</td>
                {yearFilter && (
                  <td className="py-2 px-2 text-right font-mono-num text-[var(--text-secondary)]" data-testid={`vendor-total-historic-${idx}`}>
                    {formatMoney(v.total_amount_historic ?? v.total_amount, baseCurrency)}
                  </td>
                )}
                <td className="py-2 px-2 font-mono-num text-xs text-[var(--text-secondary)]">{v.last_invoice_date || "—"}</td>
                <td className="py-2 px-2 text-center">
                  {(() => {
                    const flag = expectedByKey[v.group_key];
                    const isFlagged = !!flag;
                    const missing = isFlagged ? (flag.missing_ytd || 0) : 0;
                    const ytd = isFlagged ? (flag.ytd_invoice_months || 0) : 0;
                    const expected = isFlagged ? (flag.expected_count_ytd || 0) : 0;
                    return (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleExpected(v); }}
                        disabled={expectedBusy === v.group_key}
                        data-testid={`vendor-expected-toggle-${idx}`}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md font-medium text-xs transition-colors ${
                          isFlagged
                            ? missing > 0
                              ? "text-white bg-[var(--status-warning)] hover:opacity-90"
                              : "text-white bg-emerald-600 hover:bg-emerald-700"
                            : "text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]"
                        }`}
                        title={
                          isFlagged
                            ? (lang === "es"
                                ? (missing > 0
                                    ? `Faltan ${missing} factura(s) de ${expected} esperadas este año — clic para desactivar`
                                    : `Factura mensual al día (${ytd}/${expected}) — clic para desactivar`)
                                : (missing > 0
                                    ? `Missing ${missing} of ${expected} expected this year — click to disable`
                                    : `Monthly up to date (${ytd}/${expected}) — click to disable`))
                            : (lang === "es" ? "Marcar como factura mensual" : "Mark as monthly invoice")
                        }
                      >
                        <CalendarClock size={14} />
                        {isFlagged && (
                          <span className="hidden sm:inline font-mono-num text-[10px]">
                            {ytd}/{expected}
                          </span>
                        )}
                      </button>
                    );
                  })()}
                </td>
                <td className="py-2 px-2 text-right">
                  {!v._ghost && (
                    <button
                      onClick={(e) => { e.stopPropagation(); openEdit(v); }}
                      data-testid={`vendor-edit-${idx}`}
                      className="p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"
                      title={lang === "es" ? "Editar proveedor" : "Edit vendor"}
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                </td>
              </tr>
              {expanded[v.group_key] && (
                <tr data-testid={`vendor-months-row-${idx}`} className="bg-[var(--surface-hover)]/30">
                  <td colSpan={yearFilter ? 10 : 9} className="px-4 py-3">
                    {expanded[v.group_key].loading ? (
                      <div className="text-center text-xs text-[var(--text-muted)] py-2">…</div>
                    ) : (
                      <VendorMonthsBreakdown
                        data={expanded[v.group_key]}
                        baseCurrency={baseCurrency}
                        lang={lang}
                        vendor={v}
                        isMonthlyExpected={!!expectedByKey[v.group_key]}
                        onEditInvoice={(inv) => openInvoiceEdit(inv, v.group_key)}
                        onAddInvoice={(ven, m) => {
                          // Navigate to /invoices with prefill params via URL so
                          // the user lands directly on the upload modal with the
                          // vendor + suggested date filled in. We use the
                          // mid-month date for the suggestion to avoid edge-of-
                          // month confusion.
                          const day = "15";
                          const date = `${m.month}-${day}`;
                          const params = new URLSearchParams({
                            new_invoice: "1",
                            vendor: ven.primary_name || "",
                            vendor_tax_id: ven.vendor_tax_id || "",
                            date,
                          });
                          window.location.href = `/invoices?${params.toString()}`;
                        }}
                        onDeleteInvoice={async (inv) => {
                          // Confirmation with vendor + amount + date so the
                          // user always knows exactly what they're removing.
                          const msg = lang === "es"
                            ? `¿Eliminar la factura del ${inv.date} nº ${inv.invoice_number || "—"} por ${formatMoney(inv.amount, inv.currency || baseCurrency)}?`
                            : `Delete invoice ${inv.date} #${inv.invoice_number || "—"} for ${formatMoney(inv.amount, inv.currency || baseCurrency)}?`;
                          if (!window.confirm(msg)) return;
                          try {
                            await api.delete(`/invoices/${inv.id}`);
                            toast.success(lang === "es" ? "Factura eliminada" : "Invoice deleted");
                            await Promise.all([refreshExpanded(v.group_key), load()]);
                          } catch (err) {
                            toast.error(formatApiError(err));
                          }
                        }}
                      />
                    )}
                  </td>
                </tr>
              )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-[var(--text-muted)]">
        {lang === "es"
          ? "Tip: haz clic en la fila para ver el desglose mensual · Shift+clic para seleccionar un rango entre dos filas."
          : "Tip: click a row to see the monthly breakdown · Shift+click to select a range between two rows."}
      </p>

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) closeEdit(); }}>
        <DialogContent data-testid="vendor-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              {lang === "es" ? "Editar proveedor" : "Edit vendor"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <p className="text-xs text-[var(--text-muted)]">
                {lang === "es"
                  ? `Se actualizarán ${editing.invoice_count} facturas de este grupo.`
                  : `${editing.invoice_count} invoices in this group will be updated.`}
              </p>
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
                  {lang === "es" ? "Nombre del proveedor" : "Vendor name"}
                </label>
                <Input
                  data-testid="vendor-edit-name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder={lang === "es" ? "Ej. Mercadona S.A." : "e.g. Mercadona S.A."}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
                  CIF / NIF / VAT
                </label>
                <Input
                  data-testid="vendor-edit-tax-id"
                  value={editTaxId}
                  onChange={(e) => setEditTaxId(e.target.value)}
                  placeholder="A46103834"
                  className="font-mono"
                />
                <p className="text-[10px] text-[var(--text-muted)]">
                  {lang === "es"
                    ? "Añadir el CIF correcto permite fusionar futuras variantes automáticamente."
                    : "Adding the correct tax ID will auto-merge future spelling variants."}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeEdit}
              disabled={saving}
              data-testid="vendor-edit-cancel"
            >
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              onClick={submitEdit}
              disabled={saving}
              data-testid="vendor-edit-save"
            >
              {saving
                ? (lang === "es" ? "Guardando…" : "Saving…")
                : (lang === "es" ? "Guardar cambios" : "Save changes")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge modal */}
      <Dialog open={mergeOpen} onOpenChange={(o) => { if (!saving) setMergeOpen(o); }}>
        <DialogContent data-testid="vendor-merge-dialog">
          <DialogHeader>
            <DialogTitle>
              {lang === "es" ? "Fusionar proveedores" : "Merge vendors"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-[var(--text-muted)] space-y-1">
              <p>
                {lang === "es"
                  ? `Se fusionarán ${selectedIds.size} proveedores en uno solo. ${selectedInvoiceCount} facturas adoptarán el nombre y CIF que indiques.`
                  : `${selectedIds.size} vendors will become one. ${selectedInvoiceCount} invoices will adopt the name and tax ID you choose.`}
              </p>
              <ul className="list-disc pl-4 max-h-32 overflow-y-auto" data-testid="vendor-merge-list">
                {selectedVendors.map((v) => (
                  <li key={v.group_key} className="truncate">
                    {v.primary_name} {v.vendor_tax_id ? `(${v.vendor_tax_id})` : `— ${lang === "es" ? "sin CIF" : "no tax ID"}`} · {v.invoice_count}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
                {lang === "es" ? "Nombre destino" : "Target name"}
              </label>
              <Input
                data-testid="vendor-merge-name"
                value={mergeName}
                onChange={(e) => setMergeName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
                CIF / NIF / VAT
              </label>
              <Input
                data-testid="vendor-merge-tax-id"
                value={mergeTaxId}
                onChange={(e) => setMergeTaxId(e.target.value)}
                className="font-mono"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMergeOpen(false)}
              disabled={saving}
              data-testid="vendor-merge-cancel"
            >
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              onClick={submitMerge}
              disabled={saving}
              data-testid="vendor-merge-save"
            >
              {saving
                ? (lang === "es" ? "Fusionando…" : "Merging…")
                : (lang === "es" ? "Fusionar" : "Merge")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add manual vendor */}
      <Dialog open={manualOpen} onOpenChange={(o) => { if (!o && !saving) { setManualOpen(false); } }}>
        <DialogContent data-testid="vendor-add-manual-dialog">
          <DialogHeader>
            <DialogTitle>{lang === "es" ? "Añadir proveedor" : "Add vendor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-[var(--text-muted)]">
              {lang === "es"
                ? "Crea un proveedor antes de subir su primera factura. Útil para marcar de antemano los recurrentes (luz, alquiler, gestoría…)."
                : "Create a vendor before uploading its first invoice. Useful to pre-flag recurring ones (utilities, rent, accountant…)."}
            </p>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
                {lang === "es" ? "Nombre del proveedor" : "Vendor name"}
              </label>
              <Input
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                placeholder={lang === "es" ? "Ej. Iberdrola" : "e.g. Iberdrola"}
                data-testid="vendor-add-manual-name"
                autoComplete="off"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-[var(--text-muted)]">
                CIF / NIF / VAT
              </label>
              <Input
                value={manualTaxId}
                onChange={(e) => setManualTaxId(e.target.value.toUpperCase().replace(/\s+/g, ""))}
                placeholder="B12345678"
                className="font-mono"
                data-testid="vendor-add-manual-tax-id"
                autoComplete="off"
              />
            </div>
            <label className="flex items-center gap-2 text-sm select-none cursor-pointer">
              <input
                type="checkbox"
                checked={manualRecurring}
                onChange={(e) => setManualRecurring(e.target.checked)}
                className="rounded border-[var(--border-color)]"
                data-testid="vendor-add-manual-recurring"
              />
              <span>
                {lang === "es" ? "Marcar como factura mensual" : "Mark as monthly invoice"}
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setManualOpen(false)}
              disabled={saving}
              data-testid="vendor-add-manual-cancel"
            >
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              onClick={submitManual}
              disabled={saving || (!manualName.trim() && !manualTaxId.trim())}
              className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
              data-testid="vendor-add-manual-save"
            >
              {saving
                ? (lang === "es" ? "Guardando…" : "Saving…")
                : (lang === "es" ? "Añadir" : "Add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter 82.7s — EditInvoiceModal in-place: evita salir de Vendors y
         perder el expandido del proveedor al editar una factura. */}
      {editingInvoice && (
        <EditInvoiceModal
          invoice={editingInvoice}
          categories={[]}
          onClose={() => { setEditingInvoice(null); setEditingVendorKey(null); }}
          onSaved={async () => {
            const key = editingVendorKey;
            setEditingInvoice(null);
            setEditingVendorKey(null);
            // Refresca sin colapsar el proveedor abierto + totales globales.
            if (key) {
              await Promise.all([refreshExpanded(key), load()]);
            } else {
              await load();
            }
          }}
          onCategoriesChange={() => {}}
        />
      )}
      {editingLoading && !editingInvoice && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm"
          data-testid="vendor-invoice-loading"
        >
          <div className="rounded-lg bg-[var(--surface)] px-6 py-4 shadow-xl text-sm text-[var(--text-secondary)]">
            {lang === "es" ? "Cargando factura…" : "Loading invoice…"}
          </div>
        </div>
      )}
    </div>
  );
}

